import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "sonner";
import { Check, ChevronsUpDown } from "lucide-react";

// Local YYYY-MM-DD — avoids UTC shifting "today" to tomorrow late at night.
function localTodayISO() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const TYPES = ["Intro Email","Follow-up Email","LinkedIn Request","LinkedIn Message","Call Attempt","Live Conversation","Voicemail","Meeting Scheduled","Submission Received","Market Overview Sent","Touching base","Referral / Intro"];
const CHANNELS = ["Email","Call","LinkedIn","Meeting","Referral","Other"];
const OUTCOMES = ["No Response","Awaiting Reply","Connected","Meeting Set","Submission Received","Nurture","Not a Fit","Active"];
const ACTIONS = ["Send intro email","Call office","Leave voicemail","Send follow-up email","Send market overview","Schedule intro call","Book in-person meeting","Review submission","Misc. discussion","Move to nurture","Stay in touch"];

export default function LogActivityForm({ onSuccess, onCancel, defaultBrokerId }) {
    const [brokers, setBrokers] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        broker_id: defaultBrokerId || "",
        activity_date: localTodayISO(),
        activity_type: "Follow-up Email",
        channel: "Email",
        outcome: "Awaiting Reply",
        next_step: "Send follow-up email",
        next_due_date: "",
        notes: "",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/brokers").then((r) => setBrokers(r.data)).catch(() => {});
    }, []);

    const selected = brokers.find((b) => b.id === form.broker_id);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.broker_id) { toast.error("Pick a broker"); return; }
        setSaving(true);
        try {
            const { data } = await api.post("/activities", form);
            toast.success("Activity logged");
            onSuccess?.(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to log activity");
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="log-activity-form">
            <Field label="Broker / agency *">
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            type="button"
                            variant="outline"
                            role="combobox"
                            className="w-full justify-between h-10 font-normal"
                            data-testid="broker-picker-trigger"
                        >
                            {selected ? `${selected.agency_name} — ${selected.producer_name}` : "Select broker…"}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command>
                            <CommandInput placeholder="Search brokers…" data-testid="broker-picker-search" />
                            <CommandList>
                                <CommandEmpty>No broker found.</CommandEmpty>
                                <CommandGroup>
                                    {brokers.map((b) => (
                                        <CommandItem
                                            key={b.id}
                                            value={`${b.agency_name} ${b.producer_name}`}
                                            onSelect={() => {
                                                setForm((f) => ({ ...f, broker_id: b.id }));
                                                setOpen(false);
                                            }}
                                            data-testid={`broker-option-${b.id}`}
                                        >
                                            <Check className={`mr-2 h-4 w-4 ${form.broker_id === b.id ? "opacity-100" : "opacity-0"}`} />
                                            <div>
                                                <div className="font-medium text-ph-text">{b.agency_name}</div>
                                                <div className="text-xs text-ph-muted">{b.producer_name} · {b.location}</div>
                                            </div>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
                {selected && (
                    <div className="text-xs text-ph-muted mt-1.5">
                        Producer auto-filled: <span className="text-ph-text font-medium">{selected.producer_name}</span>
                    </div>
                )}
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Activity date *">
                    <Input type="date" required value={form.activity_date} onChange={(e) => setForm({ ...form, activity_date: e.target.value })} data-testid="activity-date-input" />
                </Field>
                <Field label="Activity type *">
                    <Select value={form.activity_type} onValueChange={(v) => setForm({ ...form, activity_type: v })}>
                        <SelectTrigger data-testid="activity-type-trigger"><SelectValue /></SelectTrigger>
                        <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                </Field>
                <Field label="Channel *">
                    <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                        <SelectTrigger data-testid="activity-channel-trigger"><SelectValue /></SelectTrigger>
                        <SelectContent>{CHANNELS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                </Field>
                <Field label="Outcome *">
                    <Select value={form.outcome} onValueChange={(v) => setForm({ ...form, outcome: v })}>
                        <SelectTrigger data-testid="activity-outcome-trigger"><SelectValue /></SelectTrigger>
                        <SelectContent>{OUTCOMES.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                </Field>
                <Field label="Next step">
                    <Select value={form.next_step} onValueChange={(v) => setForm({ ...form, next_step: v })}>
                        <SelectTrigger data-testid="activity-nextstep-trigger"><SelectValue /></SelectTrigger>
                        <SelectContent>{ACTIONS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                    </Select>
                </Field>
                <Field label="Next due date">
                    <Input type="date" value={form.next_due_date} onChange={(e) => setForm({ ...form, next_due_date: e.target.value })} data-testid="activity-nextdue-input" />
                </Field>
                <Field label="Notes" full>
                    <Textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="activity-notes-input" placeholder="What happened? Pending items? Pipeline impact?" />
                </Field>
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t border-ph-borderSoft">
                {onCancel && (
                    <Button type="button" variant="outline" onClick={onCancel} className="font-medium" data-testid="cancel-log-activity">Cancel</Button>
                )}
                <Button type="submit" disabled={saving} className="bg-ph-primary text-white font-medium hover:bg-ph-primaryHover" data-testid="submit-log-activity">
                    {saving ? "Logging…" : "Log activity"}
                </Button>
            </div>
        </form>
    );
}

function Field({ label, children, full }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <label className="text-xs font-medium text-ph-text mb-1.5 block">{label}</label>
            {children}
        </div>
    );
}
